import { useRef } from "react";
import { Trophy, ArrowLeft, ArrowRight } from "lucide-react";
import { FadeUp } from "./Reveal";
import { AWARDS } from "../data/content";
import { useLang } from "../i18n/i18n";

export function AwardGallery() {
  const scrollRef = useRef(null);
  const { t } = useLang();

  const scrollBy = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 420, behavior: "smooth" });
    }
  };

  return (
    <section
      data-testid="award-gallery"
      className="relative overflow-hidden bg-ink px-6 py-28 md:px-12 md:py-36"
    >
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[500px] w-[500px] rounded-full bg-crimson/10 blur-[150px]" />
      <div className="pointer-events-none absolute right-0 bottom-0 h-[400px] w-[400px] rounded-full bg-bluecore/20 blur-[140px]" />
      <span
        className="pointer-events-none absolute -top-4 right-6 select-none font-serif text-[16vw] leading-none text-stroke md:right-16"
        aria-hidden="true"
      >
        2026
      </span>

      <div className="relative mx-auto max-w-[1500px]">
        <FadeUp>
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <div>
              <div className="mb-6 flex items-center gap-4">
                <span className="h-px w-12 bg-crimson" />
                <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">
                  {t.award.kicker}
                </span>
              </div>
              <h2 className="font-serif text-4xl font-light leading-tight text-white md:text-6xl">
                {t.award.heading}
              </h2>
              <p className="mt-5 max-w-xl leading-relaxed text-white/55">
                {t.award.sub}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="award-prev"
                onClick={() => scrollBy(-1)}
                className="flex h-12 w-12 items-center justify-center border border-white/20 text-white transition-colors duration-300 hover:border-crimson hover:bg-crimson"
                aria-label="Previous"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                data-testid="award-next"
                onClick={() => scrollBy(1)}
                className="flex h-12 w-12 items-center justify-center border border-white/20 text-white transition-colors duration-300 hover:border-crimson hover:bg-crimson"
                aria-label="Next"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </FadeUp>

        <div
          ref={scrollRef}
          data-testid="award-scroll"
          className="mt-14 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {AWARDS.map((a, i) => (
            <div
              key={i}
              data-testid={`award-card-${i}`}
              className="group relative w-[300px] shrink-0 snap-start overflow-hidden border border-white/10 bg-ink2 transition-colors duration-400 hover:border-crimson/60 sm:w-[400px]"
            >
              <div className="relative aspect-[3/2] overflow-hidden">
                <img
                  src={a.image}
                  alt={a.title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink2 via-ink2/20 to-transparent" />
                <div className="absolute left-4 top-4 flex items-center gap-2 border border-white/10 bg-ink/80 px-3 py-1.5 backdrop-blur-md">
                  <Trophy className="h-3.5 w-3.5 text-crimson" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                    {a.year}
                  </span>
                </div>
              </div>
              <div className="p-7">
                <h3 className="font-serif text-2xl font-light leading-snug text-white">
                  {a.title}
                </h3>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-crimson">
                  {a.org}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-white/55">
                  {a.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
